import { useState } from 'react';
import { MessageSquare, Plus, Trash2, X } from 'lucide-react';
import { Button, ConfirmModal } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { ChatSession } from './types';

function formatSessionDate(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Session list with "new chat" and delete. Off-canvas drawer below lg, fixed column from lg. */
export function ChatSidebar({
  open,
  onClose,
  showSessions,
  sessions,
  currentSessionId,
  onSelect,
  onNewChat,
  onDelete,
}: {
  open: boolean;
  onClose: () => void;
  /** Sessions are only stored for signed-in users. */
  showSessions: boolean;
  sessions: ChatSession[];
  currentSessionId: string | null;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  onDelete: (id: string) => Promise<void> | void;
}) {
  const [toDelete, setToDelete] = useState<ChatSession | null>(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await onDelete(toDelete.id);
    } finally {
      setDeleting(false);
      setToDelete(null);
    }
  };

  return (
    <>
      {open && <div className="fixed inset-0 z-40 bg-sl-charcoal/40 lg:hidden" onClick={onClose} aria-hidden />}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 shrink-0 flex-col border-r border-border bg-card transition-transform duration-200 ease-out',
          'lg:static lg:z-auto lg:translate-x-0',
          open ? 'translate-x-0 shadow-pop lg:shadow-none' : '-translate-x-full',
        )}
        aria-label="Gesprekken"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold">Gesprekken</h2>
          <Button variant="ghost" size="icon" className="h-8 w-8 lg:hidden" onClick={onClose} aria-label="Zijbalk sluiten">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-3">
          <Button
            variant="brand"
            className="w-full"
            onClick={() => {
              onNewChat();
              onClose();
            }}
          >
            <Plus className="h-4 w-4" />
            Nieuw gesprek
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
          {!showSessions ? (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">Log in om je gesprekken te bewaren.</p>
          ) : sessions.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-3 py-8 text-center">
              <MessageSquare className="h-5 w-5 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Nog geen eerdere gesprekken</p>
            </div>
          ) : (
            <>
              <p className="px-2 pb-1.5 pt-1 text-xs font-medium text-muted-foreground">
                Eerdere gesprekken ({sessions.length})
              </p>
              <ul className="space-y-0.5">
                {sessions.map((session) => {
                  const active = currentSessionId === session.id;
                  return (
                    <li key={session.id} className="group relative">
                      <button
                        type="button"
                        onClick={() => onSelect(session.id)}
                        className={cn(
                          'w-full rounded-md border-l-2 py-2 pl-3 pr-9 text-left transition-colors',
                          active ? 'border-sl-yellow bg-accent' : 'border-transparent hover:bg-muted',
                        )}
                        aria-current={active ? 'true' : undefined}
                      >
                        <span className="block truncate text-sm font-medium text-foreground">{session.title || 'Naamloos gesprek'}</span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          {formatSessionDate(session.updated_at)} · {session.message_count}{' '}
                          {session.message_count === 1 ? 'bericht' : 'berichten'}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setToDelete(session)}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1.5 text-muted-foreground opacity-100 transition-opacity hover:bg-[#FDECEC] hover:text-destructive focus:opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                        aria-label={`Gesprek "${session.title}" verwijderen`}
                        title="Verwijderen"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </aside>

      <ConfirmModal
        open={toDelete !== null}
        title="Gesprek verwijderen?"
        message={
          <>
            Het gesprek <span className="font-medium text-foreground">"{toDelete?.title}"</span> wordt definitief verwijderd.
          </>
        }
        confirmLabel="Verwijderen"
        tone="danger"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </>
  );
}
